import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  ChoiceList,
  Checkbox,
  Divider,
  Box,
} from '@shopify/polaris';
import {
  useGoogleAdsConfig,
  useGoogleAdsAuthUrl,
  useGoogleAdsAccounts,
  useSelectGoogleAdsAccount,
  useGoogleAdsConversionActions,
  useSelectConversionAction,
  useCreateConversionAction,
  useToggleGoogleAds,
  useToggleEnhancedConversions,
  useEnhancedConversionsStatus,
  useConfiguredActions,
  useEnsureAction,
  useToggleAction,
  useTestGoogleAds,
  useDisconnectGoogleAds,
  useGoogleAdsConflicts,
  useFixPrimaryConflicts,
  useRestorePrimary,
  useOnboardingStatus,
  type PrimaryConflict,
} from '../hooks/api';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { useOAuthCallbackError } from '../hooks/useOAuthCallbackError';

function StatusBadge({ status, t }: { status: string; t: ReturnType<typeof useTranslation> }) {
  switch (status) {
    case 'connected':
      return <Badge tone="success">{t.common.connected}</Badge>;
    case 'warning':
      return <Badge tone="warning">{t.common.warning}</Badge>;
    case 'error':
      return <Badge tone="critical">{t.common.error}</Badge>;
    case 'disabled':
      return <Badge>{t.common.disabled}</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

export function GoogleAds() {
  const { data: config, isLoading } = useGoogleAdsConfig();
  const { data: onboarding } = useOnboardingStatus();
  const authUrlMutation = useGoogleAdsAuthUrl();
  const {
    data: accountsData,
    refetch: refetchAccounts,
    error: accountsError,
    isFetching: accountsFetching,
  } = useGoogleAdsAccounts();
  const selectAccountMutation = useSelectGoogleAdsAccount();
  const { data: actionsData, refetch: fetchActions } = useGoogleAdsConversionActions();
  const selectActionMutation = useSelectConversionAction();
  const createActionMutation = useCreateConversionAction();
  const toggleMutation = useToggleGoogleAds();
  const toggleECMutation = useToggleEnhancedConversions();
  const { data: ecStatus } = useEnhancedConversionsStatus(
    Boolean(config?.connected && config?.enhancedConversionsEnabled)
  );
  const { data: configuredActionsData } = useConfiguredActions(
    Boolean(config?.connected && config?.hasAccount)
  );
  const ensureActionMutation = useEnsureAction();
  const toggleActionMutation = useToggleAction();
  const testMutation = useTestGoogleAds();
  const disconnectMutation = useDisconnectGoogleAds();
  const t = useTranslation();
  const { language } = useLanguage();

  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  // Opt-in cleanup on disconnect: set OUR conversion actions to Removed in the
  // merchant's Google Ads account (default OFF - see disconnect modal).
  const [removeActionsOnDisconnect, setRemoveActionsOnDisconnect] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState<string[]>([]);
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Surface OAuth cancel/failure from the callback redirect (?error=google_ads_*).
  useEffect(() => {
    const oauthErr = new URLSearchParams(window.location.search).get('error');
    if (oauthErr) {
      setActionError(
        /access_denied|user_denied|cancell?ed/i.test(oauthErr)
          ? 'The Google connection was canceled. Nothing was changed - you can connect again anytime.'
          : `The Google connection could not be completed (${oauthErr.replace(/^google_ads_/, '')}). Please try again.`
      );
    }
  }, []);
  // Surface OAuth-callback failures delivered via ?error= (e.g. partial consent).
  useOAuthCallbackError(setActionError);

  // Anti-double-counting: other PRIMARY purchase actions in the account.
  // The dismissal is keyed to the SET of conflicting actions (signature), so a
  // new or different conflict appearing later re-triggers the warning instead
  // of being silenced forever by an old "Keep as is".
  const CONFLICT_DISMISS_KEY = 'tf_gads_conflict_dismissed';
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(
    () => localStorage.getItem(CONFLICT_DISMISS_KEY)
  );
  const [showFixModal, setShowFixModal] = useState(false);
  const [demotedUndo, setDemotedUndo] = useState<PrimaryConflict[] | null>(null);
  const { data: conflictsData } = useGoogleAdsConflicts(
    Boolean(config?.connected && config?.purchaseActionId)
  );
  const fixPrimaryMutation = useFixPrimaryConflicts();
  const restorePrimaryMutation = useRestorePrimary();
  const conflicts = conflictsData?.conflicts || [];
  const conflictSignature = conflicts.map((c) => c.resourceName).sort().join('|');
  const conflictDismissed = conflictSignature.length > 0 && dismissedSignature === conflictSignature;

  const handleFixPrimary = useCallback(async () => {
    setActionError(null);
    try {
      const result = await fixPrimaryMutation.mutateAsync();
      // An empty demoted list (conflict resolved externally in the meantime)
      // must not wedge the UI into a no-banner state.
      setDemotedUndo(result.demoted.length > 0 ? result.demoted : null);
      setShowFixModal(false);
    } catch (error: any) {
      setShowFixModal(false);
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [fixPrimaryMutation, t]);

  const handleUndoFix = useCallback(async () => {
    if (!demotedUndo?.length) return;
    try {
      await restorePrimaryMutation.mutateAsync(demotedUndo.map((c) => c.resourceName));
      setDemotedUndo(null);
    } catch (error: any) {
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [demotedUndo, restorePrimaryMutation, t]);

  const handleConnect = useCallback(async () => {
    setActionError(null);
    try {
      const result = await authUrlMutation.mutateAsync();
      // Use same-page redirect instead of window.open to avoid popup blockers
      // (await breaks the user gesture chain, causing browsers to block popups)
      if (window.top) {
        window.top.location.href = result.authUrl;
      } else {
        window.location.href = result.authUrl;
      }
    } catch (error: any) {
      console.error('Failed to get auth URL:', error);
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [authUrlMutation, t]);

  // Open the account picker and (re)fetch the accessible accounts on demand.
  const openAccountPicker = useCallback(() => {
    setShowAccountModal(true);
    refetchAccounts();
  }, [refetchAccounts]);

  // After OAuth we land connected but without an account selected yet
  // (?step=select-account). Auto-open the picker once so the merchant can pick.
  const pickerAutoOpened = useRef(false);
  useEffect(() => {
    if (config?.connected && !config.hasAccount && !pickerAutoOpened.current) {
      pickerAutoOpened.current = true;
      openAccountPicker();
    }
  }, [config?.connected, config?.hasAccount, openAccountPicker]);

  const handleSelectAccount = useCallback(async () => {
    const account = (accountsData?.accounts || []).find((a) => a.customerId === selectedAccount[0]);
    if (!account) return;
    setActionError(null);
    try {
      await selectAccountMutation.mutateAsync({
        customerId: account.customerId,
        customerName: account.descriptiveName,
        currencyCode: account.currencyCode,
        loginCustomerId: account.loginCustomerId,
      });
      setShowAccountModal(false);
      setSelectedAccount([]);
      fetchActions();
      setShowActionModal(true);
    } catch (error: any) {
      console.error('Failed to select account:', error);
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [selectedAccount, accountsData, selectAccountMutation, fetchActions, t]);

  const handleSelectAction = useCallback(async () => {
    if (!selectedAction[0]) return;
    setActionError(null);
    try {
      await selectActionMutation.mutateAsync(selectedAction[0]);
      setShowActionModal(false);
      setSelectedAction([]);
    } catch (error: any) {
      console.error('Failed to select action:', error);
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [selectedAction, selectActionMutation, t]);

  const handleCreateAction = useCallback(async () => {
    setActionError(null);
    try {
      await createActionMutation.mutateAsync();
      setShowActionModal(false);
    } catch (error: any) {
      console.error('Failed to create action:', error);
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [createActionMutation, t]);

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleMutation.mutateAsync(enabled);
        setShowDisableModal(false);
      } catch (error: any) {
        console.error('Failed to toggle:', error);
        setActionError(error.message || t.googleAds.actionFailed);
      }
    },
    [toggleMutation, t]
  );

  const handleToggleEC = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleECMutation.mutateAsync(enabled);
      } catch (error: any) {
        console.error('Failed to toggle EC:', error);
        setActionError(error.message || t.googleAds.actionFailed);
      }
    },
    [toggleECMutation, t]
  );

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      // The route always responds HTTP 200; the REAL outcome is in the body.
      // (An upload rejected by Google used to display as "Test successful".)
      const result = await testMutation.mutateAsync();
      if (result.success) {
        // Show Google's acknowledgment id when present (Data Manager path):
        // the immediate Google-side proof of receipt.
        const message = result.requestId
          ? `${t.googleAds.testSuccess} ${t.googleAds.testRequestId}: ${result.requestId}`
          : t.googleAds.testSuccess;
        setTestResult({ success: true, message });
      } else {
        setTestResult({ success: false, message: result.error || t.googleAds.testFailed });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || t.googleAds.testFailed });
    }
  }, [testMutation, t]);

  // Activate/create a conversion action - failures MUST surface (a silent
  // .mutate() left the merchant with a button that "does nothing" on error).
  const handleEnsureAction = useCallback(async (eventType: string) => {
    setActionError(null);
    try {
      await ensureActionMutation.mutateAsync(eventType);
    } catch (error: any) {
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [ensureActionMutation, t]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync({ removeActions: removeActionsOnDisconnect });
      setShowDisconnectModal(false);
      setRemoveActionsOnDisconnect(false);
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      setActionError(error.message || t.googleAds.actionFailed);
    }
  }, [disconnectMutation, removeActionsOnDisconnect, t]);

  if (isLoading) {
    return (
      <Page title={t.googleAds.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const accounts = accountsData?.accounts || [];
  const actions = actionsData?.actions || [];

  return (
    <Page
      title={t.googleAds.title}
      subtitle={t.googleAds.subtitle}
      primaryAction={
        config?.connected
          ? {
              content: config.isEnabled ? t.googleAds.disableTracking : t.googleAds.enableTracking,
              // Disabling pauses delivery to Google - worth a confirmation
              // (capture continues; re-enable backfills). Enabling is direct.
              onAction: () => (config.isEnabled ? setShowDisableModal(true) : handleToggle(true)),
              loading: toggleMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Action Error Banner */}
        {actionError && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setActionError(null)}>
              <p>{actionError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Double-counting conflict: other PRIMARY purchase actions in the account */}
        {conflicts.length > 0 && !conflictDismissed && !demotedUndo && (
          <Layout.Section>
            <Banner
              tone="warning"
              title={t.googleAds.conflictTitle}
              action={{
                content: t.googleAds.conflictFix,
                onAction: () => setShowFixModal(true),
              }}
              secondaryAction={{
                content: t.googleAds.conflictKeep,
                onAction: () => {
                  localStorage.setItem(CONFLICT_DISMISS_KEY, conflictSignature);
                  setDismissedSignature(conflictSignature);
                },
              }}
            >
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">{t.googleAds.conflictDesc}</Text>
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  {conflicts.map((c) => c.name).join(', ')}
                </Text>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {/* Post-fix confirmation with undo */}
        {demotedUndo && demotedUndo.length > 0 && (
          <Layout.Section>
            <Banner
              tone="success"
              title={t.googleAds.conflictFixed}
              action={{
                content: t.googleAds.conflictUndo,
                onAction: handleUndoFix,
                loading: restorePrimaryMutation.isPending,
              }}
              onDismiss={() => setDemotedUndo(null)}
            >
              <p>{demotedUndo.map((c) => c.name).join(', ')}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Error Banner */}
        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title={t.common.warning}>
              <p>{config.lastErrorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Test Result */}
        {testResult && (
          <Layout.Section>
            <Banner
              tone={testResult.success ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t.googleAds.connectionStatus}
                </Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} t={t} />}
              </InlineStack>

              {config?.connected && config.hasAccount ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {t.googleAds.account}:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {config.customerName} ({config.customerId})
                    </Text>
                  </InlineStack>

                  {config.purchaseActionName && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        {t.googleAds.conversionAction}:
                      </Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {config.purchaseActionName}
                      </Text>
                    </InlineStack>
                  )}

                  {config.lastSyncAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        {t.googleAds.lastSync}:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSyncAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>
                      {t.googleAds.testConnection}
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      {t.common.disconnect}
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : config?.connected ? (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {t.googleAds.selectAccountPrompt}
                  </Text>
                  <InlineStack gap="200">
                    <Button variant="primary" onClick={openAccountPicker} loading={accountsFetching}>
                      {t.common.selectAccount}
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      {t.common.disconnect}
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    {t.googleAds.connectDescription}
                  </Text>
                  <Box>
                    <Button
                      variant="primary"
                      onClick={handleConnect}
                      loading={authUrlMutation.isPending}
                    >
                      {t.googleAds.connectGoogleAds}
                    </Button>
                  </Box>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Conversion actions (full funnel) */}
        {config?.connected && config?.hasAccount && (() => {
          const configured = configuredActionsData?.actions ?? [];
          const supported = configuredActionsData?.supported ?? [];
          const byEvent: Record<string, typeof configured[number]> = {};
          for (const a of configured) byEvent[a.eventType] = a;
          const primary = byEvent['PURCHASE'];
          const secondaries = supported.filter((s) => !s.isPrimary);
          const funnelCopy: Record<string, { label: string; explain: string }> = {
            PRODUCT_VIEW: { label: t.googleAds.funnelProductView, explain: t.googleAds.funnelProductViewDesc },
            ADD_TO_CART: { label: t.googleAds.funnelAddToCart, explain: t.googleAds.funnelAddToCartDesc },
            BEGIN_CHECKOUT: { label: t.googleAds.funnelBeginCheckout, explain: t.googleAds.funnelBeginCheckoutDesc },
            ADD_PAYMENT_INFO: { label: t.googleAds.funnelAddPaymentInfo, explain: t.googleAds.funnelAddPaymentInfoDesc },
          };
          return (
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">{t.googleAds.conversionActionsTitle}</Text>
                  <Text as="p" variant="bodyMd" tone="subdued">{t.googleAds.conversionActionsDesc}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{t.googleAds.autoCreatedNote}</Text>
                  {/* Gate-composition clarity: the per-action toggles only take
                      effect once the GLOBAL funnel forwarding is on - without
                      this hint, "Active" checkboxes with the master switch off
                      read as if events were flowing. */}
                  {onboarding && !onboarding.funnelEnabled && (
                    <Text as="p" variant="bodySm" tone="caution">{t.googleAds.funnelOffHint}</Text>
                  )}

                  {/* Primary: purchase (required) */}
                  <Box borderColor="border" borderWidth="025" borderRadius="200" padding="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300">
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">{t.googleAds.funnelPurchase}</Text>
                          <Badge tone="info">{t.googleAds.primaryBadge}</Badge>
                        </InlineStack>
                        <Text as="span" variant="bodySm" tone="subdued">{t.googleAds.funnelPurchaseDesc}</Text>
                      </BlockStack>
                      {primary ? (
                        <Badge tone="success">{t.common.connected}</Badge>
                      ) : (
                        <Button
                          variant="primary"
                          loading={ensureActionMutation.isPending && ensureActionMutation.variables === 'PURCHASE'}
                          onClick={() => handleEnsureAction('PURCHASE')}
                        >
                          {t.googleAds.createPrimaryAction}
                        </Button>
                      )}
                    </InlineStack>
                  </Box>

                  {/* Secondary funnel actions (optional, opt-in) */}
                  {secondaries.map((s) => {
                    const copy = funnelCopy[s.eventType] || { label: s.eventType, explain: '' };
                    const row = byEvent[s.eventType];
                    const busy = ensureActionMutation.isPending && ensureActionMutation.variables === s.eventType;
                    return (
                      <Box key={s.eventType} borderColor="border" borderWidth="025" borderRadius="200" padding="300">
                        <InlineStack align="space-between" blockAlign="center" gap="300">
                          <BlockStack gap="100">
                            <Text as="span" variant="bodyMd" fontWeight="semibold">{copy.label}</Text>
                            <Text as="span" variant="bodySm" tone="subdued">{copy.explain}</Text>
                          </BlockStack>
                          {row ? (
                            <Checkbox
                              label={row.enabled ? t.common.active : t.common.disabled}
                              checked={row.enabled}
                              disabled={toggleActionMutation.isPending}
                              onChange={(checked) => toggleActionMutation.mutate({ eventType: s.eventType, enabled: checked })}
                            />
                          ) : (
                            <Button loading={busy} onClick={() => handleEnsureAction(s.eventType)}>
                              {t.googleAds.activateAction}
                            </Button>
                          )}
                        </InlineStack>
                      </Box>
                    );
                  })}
                </BlockStack>
              </Card>
            </Layout.Section>
          );
        })()}

        {/* Enhanced Conversions */}
        {config?.connected && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t.googleAds.enhancedConversions}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t.googleAds.enhancedConversionsDesc}
                </Text>
                <Checkbox
                  label={t.googleAds.enableEnhancedConversions}
                  checked={config.enhancedConversionsEnabled || false}
                  onChange={(checked) => handleToggleEC(checked)}
                  disabled={toggleECMutation.isPending}
                  helpText={t.googleAds.enhancedConversionsHelp}
                />

                {/* Readiness: Enhanced Conversions for web must be enabled by the
                    merchant in the Google Ads UI - it cannot be turned on via the
                    API. We can only READ whether the data terms were accepted. */}
                {config.enhancedConversionsEnabled && ecStatus?.checked && ecStatus.acceptedCustomerDataTerms === false && (
                  <Banner tone="warning" title={t.googleAds.ecActionRequiredTitle}>
                    <BlockStack gap="200">
                      <Text as="p" variant="bodyMd">{t.googleAds.ecActionRequiredIntro}</Text>
                      <Text as="p" variant="bodyMd">{t.googleAds.ecStep1}</Text>
                      <Text as="p" variant="bodyMd">{t.googleAds.ecStep2}</Text>
                      <Text as="p" variant="bodyMd">{t.googleAds.ecStep3}</Text>
                      <Text as="p" variant="bodyMd" tone="subdued">{t.googleAds.ecNote}</Text>
                    </BlockStack>
                  </Banner>
                )}
                {config.enhancedConversionsEnabled && ecStatus?.checked && ecStatus.acceptedCustomerDataTerms === true && (
                  <Banner tone="success">
                    <Text as="p" variant="bodyMd">{t.googleAds.ecReady}</Text>
                  </Banner>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Setup Instructions */}
        {!config?.connected && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t.googleAds.setupInstructions}
                </Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">{t.googleAds.step1}</Text>
                  <Text as="p" variant="bodyMd">{t.googleAds.step2}</Text>
                  <Text as="p" variant="bodyMd">{t.googleAds.step3}</Text>
                  <Text as="p" variant="bodyMd">{t.googleAds.step4}</Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Account Selector Modal */}
        <Modal
          open={showAccountModal}
          onClose={() => setShowAccountModal(false)}
          title={t.googleAds.selectAccountTitle}
          primaryAction={{
            content: t.common.selectAccount,
            onAction: handleSelectAccount,
            loading: selectAccountMutation.isPending,
            disabled: !selectedAccount[0],
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => setShowAccountModal(false),
            },
          ]}
        >
          <Modal.Section>
            {accountsFetching ? (
              <SkeletonBodyText lines={4} />
            ) : accountsError ? (
              <Banner tone="critical">
                <p>{(accountsError as Error).message || t.googleAds.noAccounts}</p>
              </Banner>
            ) : accounts.length > 0 ? (
              <ChoiceList
                title={t.googleAds.availableAccounts}
                choices={accounts.map((account) => ({
                  value: account.customerId,
                  label: `${account.descriptiveName} (${account.customerId})`,
                }))}
                selected={selectedAccount}
                onChange={setSelectedAccount}
              />
            ) : (
              <Text as="p" variant="bodyMd">
                {t.googleAds.noAccounts}
              </Text>
            )}
          </Modal.Section>
        </Modal>

        {/* Conversion Action Modal */}
        <Modal
          open={showActionModal}
          onClose={() => setShowActionModal(false)}
          title={t.googleAds.selectConversionTitle}
          primaryAction={{
            content: t.common.selectAction,
            onAction: handleSelectAction,
            loading: selectActionMutation.isPending,
            disabled: !selectedAction[0],
          }}
          secondaryActions={[
            {
              content: t.common.createNew,
              onAction: handleCreateAction,
              loading: createActionMutation.isPending,
            },
            {
              content: t.common.cancel,
              onAction: () => setShowActionModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd">
                {t.googleAds.selectConversionDesc}
              </Text>
              {actions.length > 0 ? (
                <ChoiceList
                  title={t.googleAds.availableActions}
                  choices={actions.map((action) => ({
                    value: action.id,
                    label: action.categoryName
                      ? `${action.name} - ${action.categoryName}`
                      : action.name,
                    // Tag-based actions (WEBPAGE etc.) reject server-side uploads;
                    // make the wrong pick impossible instead of failing later.
                    disabled: action.uploadEligible === false,
                    helpText: action.uploadEligible === false ? t.googleAds.tagBasedAction : undefined,
                  }))}
                  selected={selectedAction}
                  onChange={setSelectedAction}
                />
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t.googleAds.noActions}
                </Text>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title={t.googleAds.disconnectTitle}
          primaryAction={{
            content: t.common.disconnect,
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => setShowDisconnectModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd">
                {t.googleAds.disconnectConfirm}
              </Text>
              <Checkbox
                label={t.googleAds.disconnectRemoveActions}
                helpText={t.googleAds.disconnectRemoveActionsHelp}
                checked={removeActionsOnDisconnect}
                onChange={setRemoveActionsOnDisconnect}
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Disable Confirmation Modal - capture continues, delivery pauses,
            re-enable re-delivers what was held */}
        <Modal
          open={showDisableModal}
          onClose={() => setShowDisableModal(false)}
          title={t.googleAds.disableTitle}
          primaryAction={{
            content: t.googleAds.disableTracking,
            onAction: () => handleToggle(false),
            loading: toggleMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => setShowDisableModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">{t.googleAds.disableConfirmBody}</Text>
              <Text as="p" variant="bodyMd" tone="subdued">{t.googleAds.disableConfirmSafety}</Text>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Fix-primary Confirmation Modal (writes to the merchant's Google Ads settings) */}
        <Modal
          open={showFixModal}
          onClose={() => setShowFixModal(false)}
          title={t.googleAds.conflictTitle}
          primaryAction={{
            content: t.googleAds.conflictFix,
            onAction: handleFixPrimary,
            loading: fixPrimaryMutation.isPending,
          }}
          secondaryActions={[
            {
              content: t.common.cancel,
              onAction: () => setShowFixModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">{t.googleAds.conflictConfirmDesc}</Text>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                {conflicts.map((c) => c.name).join(', ')}
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
